<script setup lang="ts">
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { computed, onBeforeMount, ref } from "vue";
import { showLoadingToast } from "vant";
import { Moment, StoryChapter } from "@/types/entity";
import { useStory } from "@/stores/story";
import { useRouter } from "vue-router";
import MomentSelection, {
  MomentSelectionInstance,
} from "@/components/mobile/popup/MomentSelection.vue";
import api from "@/modules/api";
import { useStoryBook } from "@/stores/story-book";
import i18n from "@/locales";

// State
const storyStore = useStory();
const storyBookStore = useStoryBook();
const initialChapter = ref<Record<number, StoryChapter>>({});
const momentSelectionRef = ref<MomentSelectionInstance | null>(null);
const selectedMoments = ref<Record<number, Moment>>({});
const router = useRouter();
const storyUuid = computed<string | null>(() => {
  return (router.currentRoute.value.query?.story as string) ?? null;
});
const storyId = ref(0);
// Lifecycle
const prepareData = async () => {
  if (storyUuid.value) {
    const story = await storyStore.getStoryItem(storyUuid.value!, true);
    storyId.value = story.id;
    initialChapter.value = story.storyChapterzz.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {} as Record<number, StoryChapter>);
    selectedMoments.value = story.storyChapterzz.reduce((acc, cur) => {
      acc[cur.momentId] = cur.moment!;
      return acc;
    }, {} as Record<number, Moment>);
  }
};
onBeforeMount(() => {
  prepareData();
});
// Event Handler
const onGenerateClick = async () => {
  try {
    storyBookStore.form.momentIdzz!.push(
      ...Object.keys(selectedMoments.value).map(Number)
    );
    // 去重
    storyBookStore.form.momentIdzz = Array.from(
      new Set(storyBookStore.form.momentIdzz)
    );
    router.back();
  } finally {
  }
};
const onMomentRelationUpdate = async () => {
  // 1. 计算出新增chapter和删除chapter
  const selectedMomentIds = new Set<number>(
    Object.keys(selectedMoments.value).map(Number)
  );
  const toDeleteChapter = Object.values(initialChapter.value).filter(
    (chapter) => !selectedMomentIds.has(chapter.momentId)
  );
  const initialMomentIds = new Set<number>(
    Object.values(initialChapter.value).map((item) => item.momentId)
  );
  const toAddChapter = Object.values(selectedMoments.value).filter(
    (moment) => !initialMomentIds.has(moment.id)
  );
  // 2. 更新chapter
  if (toAddChapter.length === 0 && toDeleteChapter.length === 0) {
    router.back();
    return;
  }
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.saving"),
    duration: 0,
  });
  try {
    const promises = [];
    if (toAddChapter.length > 0) {
      for (const chapter of toAddChapter) {
        promises.push(
          api.createOneStoryChapter({
            storyId: storyId.value!,
            momentId: chapter.id,
          })
        );
      }
    }
    if (toDeleteChapter.length > 0) {
      for (const chapter of toDeleteChapter) {
        promises.push(api.deleteOneStoryChapter(chapter.id));
      }
    }
    await Promise.all(promises);
    await storyStore.getStoryItem(storyUuid.value!, true);
    storyStore.removeStoryItemFromMap(storyUuid.value!);
    router.back();
  } finally {
    loadingToast.close();
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <threads-nav
      :show-back="true"
      :enable-switch-mate="false"
      :show-center="false"
    >
      <template #center>
        <span>{{ $t("Message.selectMoment") }}</span>
      </template>
      <template #right>
        <!-- <button
          class="btn btn-sm  rounded"
          @click="momentSelectionRef!.randomSelect(5)"
        >
          随机
        </button> -->
        <button
          v-if="!storyUuid"
          class="btn btn-sm btn-primary rounded"
          :disabled="Object.keys(selectedMoments).length === 0"
          @click="onGenerateClick"
        >
          {{ $t("Buttons.addMoment") }}
        </button>
        <button
          v-else
          class="btn btn-sm btn-primary rounded"
          :disabled="Object.keys(selectedMoments).length === 0"
          @click="onMomentRelationUpdate"
        >
          {{ $t("Buttons.save") }}
        </button>
      </template>
    </threads-nav>
    <moment-selection
      v-model:selected-moments="selectedMoments"
      ref="momentSelectionRef"
    />
  </div>
</template>
